/**
 * First-party funnel tracking.
 *
 * Separate from `analytics.js` on purpose. That file talks to Firebase, whose
 * data lives in Google's console and cannot be joined to our own tables — so it
 * can never answer "of the people who first opened the app on 10 September, how
 * many started the Free percorso". This one posts to our backend, which stores
 * the events next to `master_club_subscriptions` and makes exactly that
 * question answerable in the CRM. Both run; neither replaces the other.
 *
 * THE ONE RULE: analytics must never be able to break the app. Every export
 * here swallows its own failures, nothing awaits the network on a user action,
 * and a dead endpoint costs us a data point and nothing else.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import config from "../settings/config";
import { getAuthToken } from "./authStorage";

/**
 * Bumped when the funnel itself changes shape, so the CRM can compare before
 * and after on identical step definitions. "v1" is the funnel as first measured
 * (10/09/2026). "v2" is the client's round of 14/09/2026: intro with "Salta",
 * one social button to register, the email button, three onboarding screens.
 * "v3" (17/09/2026): no path-choice screen after onboarding. An eligible member
 * lands on the Free Programme screen (free_offer_viewed); `path_choice_guest`
 * now means the link that skips the offer there ("Continua come Guest", read
 * "Esplora l'app senza iscriverti" from 18/09), and `path_choice_viewed` /
 * `path_choice_free` / `path_choice_premium` are no longer sent.
 */
export const FUNNEL_VERSION = "v3";

/**
 * Survives logout deliberately. `Logout` calls `localStorage.clear()`, and
 * letting that take the device id would count one person as two installs and
 * split their funnel across two cohorts — a logout is not a new install.
 * `preserveFunnelIdentity()` below is what protects it.
 */
export const ANONYMOUS_ID_KEY = "ds_funnel_aid";

const QUEUE_KEY = "ds_funnel_queue";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // matches Firebase's session window
const FLUSH_DEBOUNCE_MS = 5000;
const MAX_BATCH = 50; // the endpoint's cap
const MAX_QUEUE = 200; // hard stop, so a long offline spell cannot grow forever

let sessionId = null;
let sessionTouchedAt = 0;
let flushTimer = null;
let inflight = null;
let appVersion = null;
let appVersionResolved = false;

/** localStorage throws in private mode / with site data blocked. */
function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_) {
    /* storage unavailable — tracking degrades, the app does not */
  }
}

function uuid() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  // RFC 4122 v4 by hand. The backend validates the shape, so this has to be a
  // real uuid and not just a random string.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** The device id, created once and then reused for the life of the install. */
export function getAnonymousId() {
  let id = safeGet(ANONYMOUS_ID_KEY);
  if (!id) {
    id = uuid();
    safeSet(ANONYMOUS_ID_KEY, id);
  }
  return id;
}

/**
 * Called by the logout flow, which wipes localStorage wholesale.
 *
 * Returns a function that puts the device id back. Without it every logout
 * would mint a new install and the top of the funnel would inflate with people
 * who have been here all along.
 *
 * The unsent queue comes back too: events tracked just before signing out are
 * still owed to the server, and after the wipe nothing would ever send them.
 */
export function preserveFunnelIdentity() {
  const id = safeGet(ANONYMOUS_ID_KEY);
  const queue = safeGet(QUEUE_KEY);
  return () => {
    if (id) safeSet(ANONYMOUS_ID_KEY, id);
    if (queue) safeSet(QUEUE_KEY, queue);
  };
}

function getSessionId() {
  const now = Date.now();
  if (!sessionId || now - sessionTouchedAt > SESSION_TIMEOUT_MS) {
    sessionId = uuid();
  }
  sessionTouchedAt = now;
  return sessionId;
}

function platform() {
  try {
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : "web";
  } catch (_) {
    return "web";
  }
}

/**
 * Read the installed build once and cache it.
 *
 * `App.getInfo()` rejects on web, which is why the result is cached as
 * "resolved but null" rather than retried on every event.
 */
async function resolveAppVersion() {
  if (appVersionResolved) return appVersion;
  appVersionResolved = true;
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await CapacitorApp.getInfo();
      appVersion = info?.version ?? null;
    }
  } catch (_) {
    appVersion = null;
  }
  return appVersion;
}

function readQueue() {
  try {
    const raw = safeGet(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeQueue(queue) {
  try {
    let q = queue;
    if (q.length > MAX_QUEUE) {
      // Trim oldest-first — but never the first open. It is the one event the
      // top of the funnel is built on, and it is always the OLDEST item in the
      // queue, so a plain slice(-MAX_QUEUE) during a long outage would drop
      // precisely it and nothing else that matters.
      const first = q.find((item) => item.event === "app_first_open");
      q = q.filter((item) => item !== first).slice(-(MAX_QUEUE - (first ? 1 : 0)));
      if (first) q.unshift(first);
    }
    safeSet(QUEUE_KEY, JSON.stringify(q));
  } catch (_) {
    /* ignore */
  }
}

/**
 * Record one funnel event.
 *
 * Synchronous and non-blocking: it appends to a queue in localStorage and
 * schedules a flush. Persisting first is what makes the app being killed
 * mid-flight survivable — the batch goes out on the next launch instead of
 * being lost.
 *
 * @param {string} event   a name from the backend's FunnelEvent enum
 * @param {object} [props] small, non-personal properties (e.g. `{step: 3}`)
 */
export function trackFunnel(event, props) {
  try {
    if (!event) return;

    const queue = readQueue();
    queue.push({
      // Local only, never sent: lets a flush remove exactly the items the
      // server confirmed, even if more were queued while it was waiting.
      id: uuid(),
      event,
      occurred_at: new Date().toISOString(),
      properties: props && Object.keys(props).length ? props : undefined,
      session_id: getSessionId(),
    });
    writeQueue(queue);

    scheduleFlush();
  } catch (_) {
    /* analytics never throws into the app */
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushFunnel();
  }, FLUSH_DEBOUNCE_MS);
}

/**
 * Send whatever is queued. At-least-once: nothing leaves storage until the
 * server has confirmed it.
 *
 * ⚠️ WHY IT IS WRITTEN THIS WAY. The first version removed the batch from
 * storage, THEN awaited a native call, THEN sent — and never checked the
 * response. Two things followed on production on 10/09/2026:
 *
 *  - The app reached phones ~11.5 hours before the backend route existed. For
 *    that whole window the server answered 404 (582 times), the tracker counted
 *    each one as delivered, and threw the batch away. A device's first batch is
 *    the one holding `app_first_open`, so 882 of 1 134 devices never delivered
 *    it at all.
 *  - An app backgrounded during that native await lost the batch too: already
 *    out of storage, never sent.
 *
 * Now a batch is only removed on a 2xx, or on a 422 (the server will never
 * accept it, and retrying would block everything behind it). Anything else —
 * 404, 429, 5xx, offline — leaves it queued for the next flush. Re-sending is
 * safe: the server de-duplicates on (device, session, event, occurred_at).
 */
export function flushFunnel() {
  // One flush at a time. A second caller (debounce + background + identify can
  // all fire together) waits on the one already running instead of sending the
  // same batch twice.
  if (inflight) return inflight;
  // Sending now makes a pending debounced flush redundant.
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  inflight = sendQueued().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function sendQueued() {
  try {
    // Resolved before the queue is read. The first call is a native bridge
    // round-trip; nothing is at risk while it is pending any more, but there is
    // no reason to hold it open inside the send either.
    const version = await resolveAppVersion();

    const queue = readQueue();
    if (!queue.length) return;

    // Items queued by a build older than this one carry no id. Give them one
    // and persist it first, or they could never be matched for removal and
    // would be re-sent on every flush forever.
    let patched = false;
    for (const item of queue) {
      if (!item.id) {
        item.id = uuid();
        patched = true;
      }
    }
    if (patched) writeQueue(queue);

    const batch = queue.slice(0, MAX_BATCH);
    const bySession = batch.reduce((acc, item) => {
      const key = item.session_id || "unknown";
      (acc[key] = acc[key] || []).push(item);
      return acc;
    }, {});

    const token = getAuthToken();
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    // When a token exists the server attributes the events to that member; it
    // never takes an account id from the body.
    if (token) headers.Authorization = `Bearer ${token}`;

    const outcomes = await Promise.all(
      Object.entries(bySession).map(async ([session, events]) => {
        try {
          const res = await fetch(`${config.env[config.env.mode].url}/api/v1/app-events/track`, {
            method: "POST",
            headers,
            // keepalive lets the request outlive the page, which is exactly
            // when a session's last batch is sent. If the page is gone before
            // the answer arrives, the items simply stay queued and go again on
            // the next launch — the server drops the duplicate.
            keepalive: true,
            body: JSON.stringify({
              anonymous_id: getAnonymousId(),
              session_id: session,
              platform: platform(),
              app_version: version,
              funnel_version: FUNNEL_VERSION,
              events: events.map(({ id, session_id, ...rest }) => rest),
            }),
          });
          return res.ok || res.status === 422 ? events.map((e) => e.id) : [];
        } catch (_) {
          return []; // offline / aborted: keep for the next flush
        }
      })
    );

    const delivered = new Set(outcomes.flat());
    if (!delivered.size) return;

    // Re-read rather than reuse `queue`: anything tracked while the requests
    // were in flight is in storage now and has to survive this write.
    const remaining = readQueue().filter((item) => !delivered.has(item.id));
    writeQueue(remaining);

    // Keep draining while it works — a backlog after an outage goes out 50 at a
    // time. Deliberately NOT rescheduled when nothing got through: retrying a
    // failing endpoint every few seconds from every phone is how an outage
    // turns into a flood. The next event, backgrounding, or launch will retry.
    if (remaining.length) scheduleFlush();
  } catch (_) {
    /* analytics never throws into the app */
  }
}

/**
 * Bind this device to the account that just signed in or registered.
 *
 * Flushes first so the events sent while anonymous are already on the server
 * when the stitch happens — the identify call back-fills `user_id` onto rows
 * that exist, so anything still sitting in the queue would stay unattributed.
 */
export async function identifyFunnelUser() {
  try {
    const token = getAuthToken();
    if (!token) return;

    await flushFunnel();

    await fetch(`${config.env[config.env.mode].url}/api/v1/app-events/identify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ anonymous_id: getAnonymousId() }),
    });
  } catch (_) {
    /* ignore */
  }
}

/**
 * Report the very first launch of this install, exactly once.
 *
 * Guarded on the presence of the stored id rather than on a separate flag: if
 * the id had to be minted, this is the first launch by definition.
 */
export function trackFirstOpenOnce() {
  // Warm the cache off the send path; it is awaited again inside the flush.
  resolveAppVersion();
  try {
    const existing = safeGet(ANONYMOUS_ID_KEY);
    if (!existing) {
      getAnonymousId();
      trackFunnel("app_first_open");
    }
    trackFunnel("app_session_start");
  } catch (_) {
    /* ignore */
  }
}

/** Flush on background and on unload, so a session's tail is not lost. */
export function startFunnelFlushListeners() {
  try {
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) flushFunnel();
    }).catch(() => {});
  } catch (_) {
    /* not native */
  }

  try {
    window.addEventListener("beforeunload", () => flushFunnel());
    // iOS Safari and WebViews frequently never fire beforeunload; visibility
    // change is the reliable one there.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushFunnel();
    });
  } catch (_) {
    /* ignore */
  }
}

/** Test-only access to module state, mirroring analyticsScreens.js. */
export const __TEST_ONLY__ = {
  QUEUE_KEY,
  MAX_QUEUE,
  readQueue,
  reset() {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    inflight = null;
    sessionId = null;
    sessionTouchedAt = 0;
    appVersion = null;
    appVersionResolved = false;
  },
};
